import React, { Fragment } from 'react';
import { PlxsText } from '@plexlogic/plexus-ui';
import { EntriesUpload } from '../../types/DrawData';
import WizardRadioButtonGroup from '../WizardRadioButtonGroup';
import useUploadStore, { getHeaderRowColumns, useIsHeaderRow } from './useUploadStore';
import { toSentenceCase } from '../../utils';

interface HeaderRowQuestionProps {
  entriesUpload: EntriesUpload;
  questionNumber?: string; // For advanced mode numbering
  isDisabled?: boolean;
}

const HeaderRowQuestion: React.FC<HeaderRowQuestionProps> = ({
  entriesUpload,
  questionNumber,
  isDisabled = false
}) => {
  // Use typed selectors for proper store boundaries
  const headerRowAnswer = useIsHeaderRow() ?? null;
  const setHeaderRow = useUploadStore((state) => state.setHeaderRow);

  const questionLabel = questionNumber
    ? `${questionNumber}. Is the first row a header?`
    : "Is the first row a header?";

  const radioOptions = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
  ];

  const handleRadioChange = (value: string) => {
    const answer = value === 'true';
    setHeaderRow(answer);
  };

  return (
    <Fragment>
      <PlxsText size="sm" weight='bold'>{questionLabel}</PlxsText>
      <PlxsText size="xs">
        A header row contains column names instead of actual entry data.
      </PlxsText>

      {/* First row preview - Table header style */}
      {getHeaderRowColumns(entriesUpload).length > 0 && (
        <div style={{ 
          marginBottom: 'var(--plexus-space-md, 16px)', 
          marginTop: 'var(--plexus-space-md, 16px)' 
        }}>
          <PlxsText size="sm" weight="bold">
            First row preview:
          </PlxsText>
          
          {/* Simple table header container */}
          <div style={{ 
            border: '1px solid var(--plexus-border-subtle, #e5e7eb)',
            borderRadius: 'var(--plexus-radius-md, 8px)',
            overflow: 'hidden',
            backgroundColor: 'var(--plexus-surface-base, #ffffff)'
          }}>
            <div style={{ 
              display: 'flex',
              backgroundColor: 'var(--plexus-surface-subtle, #f9fafb)',
              overflowX: 'auto'
            }}>
              {getHeaderRowColumns(entriesUpload).slice(0, 5).map((cell: string, idx: number) => (
                <div
                  key={idx}
                  style={{
                    padding: 'var(--plexus-space-sm, 8px) var(--plexus-space-md, 12px)',
                    borderRight: idx < Math.min(getHeaderRowColumns(entriesUpload).length - 1, 4) 
                      ? '1px solid var(--plexus-border-subtle, #e5e7eb)' 
                      : 'none',
                    minWidth: '100px',
                    maxWidth: '150px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  <PlxsText size="sm" weight="semiBold">
                    {toSentenceCase(cell) || '(empty)'}
                  </PlxsText>
                </div>
              ))}
              {getHeaderRowColumns(entriesUpload).length > 5 && (
                <div style={{
                  padding: 'var(--plexus-space-sm, 8px) var(--plexus-space-md, 12px)',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: '60px'
                }}>
                  <PlxsText size="sm">
                    +{getHeaderRowColumns(entriesUpload).length - 5}
                  </PlxsText>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <WizardRadioButtonGroup
        dataTestId="header-row-radio-group"
        name="header-row"
        options={radioOptions}
        value={headerRowAnswer === null ? '' : headerRowAnswer.toString()}
        onChange={handleRadioChange}
        isDisabled={isDisabled}
      />
    </Fragment>
  );
};

export default HeaderRowQuestion;
